import React from "react";
import { Box, Step, StepContent, StepLabel, Stepper, Typography } from "@mui/material";
import WidgetFrame from "./WidgetFrame";
import { useWidgetMsg } from "../../../hooks/widget/useWidgetMsg";

/** `activeStep`: the `current` step, or `steps.length` when every step is done (else the first not done). */
export function activeStepOf(steps = []) {
	const current = steps.findIndex((s) => s.state === "current");
	if (current >= 0) return current;
	const open = steps.findIndex((s) => s.state !== "done");
	return open >= 0 ? open : steps.length;
}

/**
 * `progress` (spec-widget-mcp 05 Step 8): a read-only vertical stepper — no focusable control (AC-17).
 * The group's `aria-label` names the position: "Step 3 of 4: Parcel received".
 */
export default function ProgressTracker({ envelope }) {
	const { msg } = useWidgetMsg();
	const p = envelope.payload || {};
	const steps = Array.isArray(p.steps) ? p.steps : [];
	const active = activeStepOf(steps);
	const label = active >= steps.length
		? msg("widget.progress_done", "All {total} steps complete", { total: steps.length })
		: msg("widget.progress_step", "Step {current} of {total}: {label}", { current: active + 1, total: steps.length, label: steps[active].label });

	return (
		<WidgetFrame kind="progress" title={p.title}>
			<Box role="group" aria-label={label}>
				<Stepper
					activeStep={active}
					orientation="vertical"
					sx={{
						"& .MuiStepIcon-root.Mui-active, & .MuiStepIcon-root.Mui-completed": { color: "var(--w-accent)" },
						"& .MuiStepLabel-label": { fontSize: 12.5, color: "var(--w-text)" },
						"& .MuiStepLabel-label.Mui-active": { fontWeight: 500, color: "var(--w-text)" },
					}}
				>
					{steps.map((s, i) => (
						<Step key={`${s.label}-${i}`} data-testid="widget-step" data-state={s.state} completed={s.state === "done"} expanded>
							<StepLabel>{s.label}</StepLabel>
							{s.detail ? <StepContent><Typography sx={{ fontSize: 11.5 }}>{s.detail}</Typography></StepContent> : null}
						</Step>
					))}
				</Stepper>
			</Box>
			{p.footer && <Typography sx={{ fontSize: 11 }}>{p.footer}</Typography>}
		</WidgetFrame>
	);
}
